/*
  GAL - Favoris
  Stockage local (navigateur), sans compte client ni application.
  Cle localStorage : gal:wishlist:v1 -> tableau de handles produit.
*/

(() => {
  const KEY = 'gal:wishlist:v1';
  const EVENT = 'wishlist:change';

  const read = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((handle) => typeof handle === 'string' && handle.length > 0);
    } catch (error) {
      return [];
    }
  };

  const broadcast = (items) => {
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items } }));
  };

  const write = (items) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(items));
    } catch (error) {
      /* Navigation privee ou quota plein : on continue sans persister. */
    }
    broadcast(items);
  };

  const Wishlist = {
    items: () => read(),
    count: () => read().length,
    has: (handle) => read().indexOf(handle) !== -1,
    add(handle) {
      const items = read();
      if (items.indexOf(handle) === -1) {
        items.unshift(handle);
        write(items);
      }
      return true;
    },
    remove(handle) {
      const items = read();
      const index = items.indexOf(handle);
      if (index !== -1) {
        items.splice(index, 1);
        write(items);
      }
      return false;
    },
    toggle(handle) {
      return Wishlist.has(handle) ? Wishlist.remove(handle) : Wishlist.add(handle);
    },
  };

  window.Wishlist = Wishlist;

  /* Synchronisation entre onglets ouverts. */
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) broadcast(read());
  });

  /* Retour via l'historique (bfcache) : on resynchronise. */
  window.addEventListener('pageshow', () => broadcast(read()));

  const rootUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : root + '/';
  };

  /*
    Le balisage interne d'un element peut ne pas etre encore analyse quand
    connectedCallback se declenche. On resynchronise donc sur plusieurs ticks
    plutot que de memoriser les references une fois pour toutes.
  */
  const scheduleSync = (element) => {
    element.sync();
    requestAnimationFrame(() => element.sync());

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => element.sync(), { once: true });
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Bouton coeur                                                           */
  /* ---------------------------------------------------------------------- */

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.productHandle;
      if (!this.handle) return;

      this.onClick = this.onClick.bind(this);
      this.sync = this.sync.bind(this);

      this.addEventListener('click', this.onClick);
      document.addEventListener(EVENT, this.sync);
      scheduleSync(this);
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT, this.sync);
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const added = Wishlist.toggle(this.handle);

      if (added) {
        this.classList.remove('wishlist-button--pop');
        void this.offsetWidth;
        this.classList.add('wishlist-button--pop');
      }
    }

    sync() {
      const button = this.querySelector('button');
      if (!button) return;

      const active = Wishlist.has(this.handle);
      this.classList.toggle('wishlist-button--active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');

      const label = active ? this.dataset.labelRemove : this.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Compteur du header                                                     */
  /* ---------------------------------------------------------------------- */

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.sync = this.sync.bind(this);
      document.addEventListener(EVENT, this.sync);
      scheduleSync(this);
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT, this.sync);
    }

    sync() {
      const bubble = this.querySelector('[data-wishlist-bubble]');
      const text = this.querySelector('[data-wishlist-bubble-text]');
      if (!bubble || !text) return;

      const count = Wishlist.count();
      text.textContent = count > 99 ? '99+' : String(count);
      bubble.hidden = count === 0;
      this.classList.toggle('header-actions__wishlist-icon--has-items', count > 0);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Grille de la page Favoris                                              */
  /* ---------------------------------------------------------------------- */

  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this.rendered = new Map();
      this.onChange = this.onChange.bind(this);

      document.addEventListener(EVENT, this.onChange);
      this.refresh();

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.refresh(), { once: true });
      }
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT, this.onChange);
    }

    onChange() {
      this.refresh();
    }

    setState({ loading = false, empty = false }) {
      const loadingEl = this.querySelector('[data-wishlist-loading]');
      const emptyEl = this.querySelector('[data-wishlist-empty]');
      const itemsEl = this.querySelector('[data-wishlist-items]');

      if (loadingEl) loadingEl.hidden = !loading;
      if (emptyEl) emptyEl.hidden = !empty;
      if (itemsEl) itemsEl.hidden = loading || empty;
    }

    updateTotal(count) {
      const totalEl = this.querySelector('[data-wishlist-total]');
      if (!totalEl) return;

      const singular = totalEl.dataset.singular || '{{ count }}';
      const plural = totalEl.dataset.plural || '{{ count }}';
      const template = count === 1 ? singular : plural;

      totalEl.textContent = template.replace('{{ count }}', String(count));
      totalEl.hidden = count === 0;
    }

    async refresh() {
      const itemsEl = this.querySelector('[data-wishlist-items]');
      if (!itemsEl) return;

      const handles = Wishlist.items();

      /* Retire les cartes qui ne sont plus en favori. */
      this.rendered.forEach((node, handle) => {
        if (handles.indexOf(handle) === -1) {
          node.remove();
          this.rendered.delete(handle);
        }
      });

      if (handles.length === 0) {
        this.setState({ empty: true });
        this.updateTotal(0);
        return;
      }

      const missing = handles.filter((handle) => !this.rendered.has(handle));

      if (missing.length > 0) {
        if (this.rendered.size === 0) this.setState({ loading: true });

        const cards = await Promise.all(missing.map((handle) => this.fetchCard(handle)));

        cards.forEach((html, index) => {
          const handle = missing[index];

          /* Produit supprime ou introuvable : on nettoie le favori. */
          if (!html) {
            Wishlist.remove(handle);
            return;
          }

          const wrapper = document.createElement('div');
          wrapper.innerHTML = html;
          const node = wrapper.firstElementChild;
          if (!node) return;

          itemsEl.appendChild(node);
          this.rendered.set(handle, node);
        });
      }

      /* Remet les cartes dans l'ordre des favoris. */
      handles.forEach((handle) => {
        const node = this.rendered.get(handle);
        if (node) itemsEl.appendChild(node);
      });

      const total = this.rendered.size;
      this.setState({ empty: total === 0 });
      this.updateTotal(total);
    }

    async fetchCard(handle) {
      try {
        const url = rootUrl() + 'products/' + encodeURIComponent(handle) + '?section_id=wishlist-card';
        const response = await fetch(url);
        if (!response.ok) return null;

        const markup = await response.text();
        const doc = new DOMParser().parseFromString(markup, 'text/html');
        const card = doc.querySelector('[data-wishlist-card]');
        return card ? card.outerHTML : null;
      } catch (error) {
        return null;
      }
    }
  }

  if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
  if (!customElements.get('wishlist-grid')) customElements.define('wishlist-grid', WishlistGrid);
})();
